import { Component } from "@theme/component";
import { fetchConfig, onAnimationEnd, preloadImage } from "@theme/utilities";
import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  VariantUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";
import { morph } from "@theme/morph";

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ["addToCartButton"];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("pointerenter", this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener("pointerenter", this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest("form");
    if (!form?.checkValidity()) return;

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === "true";

    if (animationEnabled && !event.target.closest(".quick-add-modal")) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector(".header-actions__cart-icon");

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (
      document.createElement("fly-to-cart")
    );

    flyToCartElement.style.setProperty("background-image", `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains("atc-added")) {
      addToCartButton.classList.add("atc-added");
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove("atc-added");
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get("add-to-cart-component")) {
  customElements.define("add-to-cart-component", AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonPlaceholder - The accelerated checkout button placeholder element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ["variantId", "liveRegion"];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {{ tiers?: { buys?: { type?: string, value?: number, maxValue?: number }, gets?: { type?: string, value?: number } }[] } | null | undefined} */
  #volumeDiscountConfig;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section, dialog, product-card");
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal,
    });
    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
      { signal },
    );

    document.addEventListener("click", this.handleAddUpsellToCart.bind(this));
    this.initPurchaseTypeChange();
    this.initSellingPlanOptionChange();
    this.initDynamicPricing();
    this.updateFreeShippingBadges();

    setTimeout(() => {
      this.refactorAdditionalCheckoutButtons();
    }, 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  updatePrices() {
    const form = this.querySelector("form");
    if (!form) return;

    const id = form.getAttribute("id");
    if (!id) return;

    const priceEls = document.querySelectorAll(`[data-price][data-form-id="${id}"]`);
    if (!priceEls?.length) return;

    const formData = new FormData(form);

    const qtyMultiplier = parseInt(
      String(formData.get("qty-multiplier") || "1"),
    );
    const currentQuantity = parseInt(String(formData.get("quantity") || "1"));
    const multipliedQuantity = currentQuantity * qtyMultiplier;

    const volumeDiscountUnitPrice = parseInt(
      Array.from(priceEls)
        .find((el) => el.hasAttribute("data-volume-discount-price"))
        ?.getAttribute("data-price") || "0",
    );
    const volumeDiscountPerUnit = this.getVolumeDiscountPerUnit(
      id,
      multipliedQuantity,
      volumeDiscountUnitPrice,
    );
    const volumeDiscountTotal = volumeDiscountPerUnit * multipliedQuantity;

    document
      .querySelectorAll(`[data-volume-discount-reveal="${id}"]`)
      .forEach((revealEl) => {
        revealEl.toggleAttribute("hidden", volumeDiscountTotal <= 0);
      });

    priceEls.forEach((priceEl) => {
        const price = parseInt(priceEl.getAttribute("data-price") || "0");
        if (isNaN(price)) return;

        let totalPrice = price * multipliedQuantity;

        if (priceEl.hasAttribute("data-volume-discount-price")) {
            totalPrice = Math.max(totalPrice - volumeDiscountTotal, 0);
        } else if (priceEl.hasAttribute("data-volume-discount-saving")) {
            totalPrice += volumeDiscountTotal;
        } else if (priceEl.hasAttribute("data-volume-discount-compare")) {
            priceEl.toggleAttribute("hidden", volumeDiscountTotal <= 0);
        }

        const totalPriceFormatted = window.formatMoney(totalPrice);

        priceEl.textContent = totalPriceFormatted;
        priceEl.setAttribute("data-total", String(totalPrice));
    });

    this.updatePaymentTerms();
    this.updateFreeShippingBadges();
  }

  /**
   * @returns {number | null}
   */
  getActiveTotalPrice() {
    const id = this.querySelector("form")?.getAttribute("id");
    if (!id) return null;

    const selectedPurchaseType = document.querySelector(`input[name="purchaseType"][form="${id}"]:checked`);
    const purchaseType =
      selectedPurchaseType instanceof HTMLInputElement && selectedPurchaseType.value === "subscription"
        ? "subscription"
        : "one-time";

    const priceEl = document.querySelector(`.purchase-type-price--${purchaseType} .price[data-form-id="${id}"]`);
    if (!priceEl) return null;

    const totalPrice = parseInt(priceEl.getAttribute("data-total") || priceEl.getAttribute("data-price") || "");
    return isNaN(totalPrice) ? null : totalPrice;
  }

  updateFreeShippingBadges() {
    const id = this.querySelector("form")?.getAttribute("id");
    if (!id) return;

    const badges = document.querySelectorAll(
      `[data-free-shipping-threshold][data-form-id="${id}"][data-product-id="${this.dataset.productId}"]`,
    );
    if (!badges.length) return;

    const totalPrice = this.getActiveTotalPrice();
    if (totalPrice === null) return;

    badges.forEach((badge) => {
      const threshold = parseInt(badge.getAttribute("data-free-shipping-threshold") || "");
      if (isNaN(threshold)) return;

      badge.toggleAttribute("hidden", totalPrice < threshold);
    });
  }

  updatePaymentTerms() {
    const paymentTermsEls = this.closest(".shopify-section")?.querySelectorAll("shopify-payment-terms");
    if (!paymentTermsEls?.length) return;

    const totalPrice = this.getActiveTotalPrice();
    if (totalPrice === null) return;

    paymentTermsEls.forEach((paymentTermsEl) => {
      let meta;
      try {
        meta = JSON.parse(paymentTermsEl.getAttribute("shopify-meta") || "");
      } catch {
        return;
      }

      const variantId = paymentTermsEl.getAttribute("variant-id");
      const variant = meta?.variants?.find((/** @type {{ id: number }} */ entry) => String(entry.id) === variantId);
      if (!variant) return;

      const numberOfTerms = Number(variant.number_of_payment_terms) || 1;
      const pricePerTerm = Math.round(totalPrice / numberOfTerms);

      variant.full_price_amount = totalPrice / 100;
      variant.full_price = window.formatMoney(totalPrice);
      variant.price_per_term_amount = pricePerTerm / 100;
      variant.price_per_term = window.formatMoney(pricePerTerm);

      const updatedMeta = JSON.stringify(meta);
      if (updatedMeta !== paymentTermsEl.getAttribute("shopify-meta")) {
        paymentTermsEl.setAttribute("shopify-meta", updatedMeta);
      }
    });
  }

  /**
   * @param {string} formId
   * @param {number} quantity
   * @param {number} unitPrice
   * @returns {number}
   */
  getVolumeDiscountPerUnit(formId, quantity, unitPrice) {
    if (this.#volumeDiscountConfig === undefined) {
        const configEl = document.querySelector(`script[data-volume-discount-config="${formId}"]`);
        let config = null;

        if (configEl?.textContent) {
            try {
                config = JSON.parse(configEl.textContent);
            } catch {
                config = null;
            }
        }

        this.#volumeDiscountConfig = config;
    }

    const tiers = this.#volumeDiscountConfig?.tiers;
    if (!Array.isArray(tiers)) return 0;

    let bestMinimumQuantity = 0;
    let bestMaximumQuantity = 0;
    let discountPerUnit = 0;

    for (const tier of tiers) {
        if (tier?.buys?.type !== "minimumQuantity") continue;

        const getsType = tier?.gets?.type;
        if (getsType !== "fixedAmount" && getsType !== "percentage") continue;

        const minimumQuantity = Number(tier.buys.value);
        const amount = Number(tier.gets.value);

        if (!minimumQuantity || !amount) continue;
        if (quantity < minimumQuantity || minimumQuantity < bestMinimumQuantity) continue;

        bestMinimumQuantity = minimumQuantity;
        bestMaximumQuantity = Number(tier.buys.maxValue) || 0;
        discountPerUnit =
            getsType === "percentage"
                ? Math.round((unitPrice * amount) / 100)
                : amount;
    }

    if (bestMaximumQuantity && quantity > bestMaximumQuantity) return 0;

    return discountPerUnit;
  }

  initQtyListener() {
    const form = this.querySelector("form");
    if (!form) return;

    const id = form.getAttribute("id");
    if (!id) return;

    const qtyInput = form.querySelector('input[name="quantity"]');
    if (qtyInput) {
        qtyInput.addEventListener("quantity-selector:update", () => {
            this.updatePrices();
        });
    }

    const qtyMultiplierInputs = document.querySelectorAll(`input[name="qty-multiplier"][form="${id}"]`);
    if (qtyMultiplierInputs.length > 0) {
    qtyMultiplierInputs.forEach((input) => {
        input.addEventListener("change", () => {
                this.updatePrices();
            });
        });
    }
  }

  initDynamicPricing() {
    const enable_dynamic_pricing = this.dataset.dynamicPricing === "true";
    if (!enable_dynamic_pricing) return;

    const form = this.querySelector("form");
    if (!form) return;

    const id = form.getAttribute("id");
    if (!id) return;

    const priceEls = document.querySelectorAll(`[data-price][data-form-id="${id}"]`);
    if (!priceEls?.length) return;

    this.updatePrices();
    this.initQtyListener();
  }

  initSellingPlanOptionChange() {
    const form = this.querySelector("form");
    if (!form) return;

    const id = form.getAttribute("id");
    if (!id) return;

    const sellingPlanInputs = document.querySelectorAll(`[data-selling-plan-input][form="${id}"]`);
    if (!sellingPlanInputs?.length) return;

    const sellingPlanInputReal = document.querySelector(`[data-selling-plan-input-real][form="${id}"]`);
    if (!sellingPlanInputReal) return;

    sellingPlanInputs.forEach((input) => {
        input.addEventListener("change", () => {
            sellingPlanInputReal.value = input.value;
        });
    });
  }

  initPurchaseTypeChange() {
    const form = this.querySelector("form");
    if (!form) return;

    const id = form.getAttribute("id");
    if (!id) return;

    const purchaseTypeInputs = document.querySelectorAll(`input[name="purchaseType"][form="${id}"]`);
    if (!purchaseTypeInputs?.length) return;

    const sellingPlanInputs = document.querySelectorAll(`[data-selling-plan-input][form="${id}"]`);
    if (!sellingPlanInputs?.length) return;

    const sellingPlanInputReal = document.querySelector(`[data-selling-plan-input-real][form="${id}"]`);
    if (!sellingPlanInputReal) return;

    const sellingPlanSelector = document.querySelector(`[data-selling-plan-selector="${id}"]`);
    if (!sellingPlanSelector) return;

    purchaseTypeInputs.forEach((input) => {
      input.addEventListener("change", (event) => {
        const value = event?.target?.value;
        if (value === "subscription") {
            sellingPlanInputReal.value =
              Array.from(sellingPlanInputs).find(
                (input) => input instanceof HTMLSelectElement || (input instanceof HTMLInputElement && input.checked),
              )?.value || "";
        } else {
            sellingPlanInputReal.value = "";
        }

        this.updatePaymentTerms();
        this.updateFreeShippingBadges();
      });
    });

    sellingPlanSelector.classList.remove("loading");
  }

  refactorAdditionalCheckoutButtons() {
    const {
      acceleratedCheckoutButtonContainer,
      acceleratedCheckoutButtonPlaceholder,
    } = this.refs;
    if (
      !acceleratedCheckoutButtonContainer ||
      !acceleratedCheckoutButtonPlaceholder
    )
      return;

    if (typeof window?.Shopify?.PaymentButton?.init === "function") {
      window.Shopify.PaymentButton.init();
    }

    const walletId = "wallet";
    const walletSingleId = "wallet-single";

    const walletElemAttr = document.getElementById(walletId)?.innerText;
    const walletElemSingleAttr =
      document.getElementById(walletSingleId)?.innerText;
    if (!walletElemAttr && !walletElemSingleAttr) {
      acceleratedCheckoutButtonPlaceholder.remove();
      acceleratedCheckoutButtonContainer.setAttribute("data-empty", "");
      return;
    }

    const walletElem = walletElemAttr
      ?.split('wallet-configs="')[1]
      ?.split('"')[0];
    const walletElemSingle = walletElemSingleAttr
      ?.split('recommended="')[1]
      ?.split('"')[0];
    const walletElemClear = walletElem?.replace(/&quot;/g, '"');
    const jsonWallet = JSON.parse(walletElemClear);

    let filteredWallet = jsonWallet?.find((item) => item.name === "apple_pay");

    if (!filteredWallet) {
      filteredWallet = jsonWallet?.find((item) => item.name === "paypal");
    }

    if (!filteredWallet) {
      filteredWallet = jsonWallet?.find((item) => item.name === "shop_pay");
    }

    const newWalletStr = JSON.stringify(filteredWallet)?.replaceAll(
      '"',
      "&quot;",
    );
    const newWallStr = document
      .getElementById(walletSingleId)
      ?.innerText?.replace(walletElemSingle, newWalletStr);

    acceleratedCheckoutButtonPlaceholder.remove();

    document
      .getElementById(walletSingleId)
      ?.insertAdjacentHTML("beforebegin", newWallStr);

    const consent = document.getElementById("shopify-buyer-consent");
    if (consent) {
      consent.remove();
    }
  }

  handleShowUpsell() {
    const { productFormButtons, upsellWrapper, upsellSuccessMessage } = this.refs;
    if (!productFormButtons || !upsellWrapper || !upsellSuccessMessage) return;

    productFormButtons.classList.add("visually-hidden-always");
    productFormButtons.classList.remove("animate-appear");
    upsellWrapper.classList.remove("visually-hidden-always");
    upsellWrapper.classList.add("animate-appear");

    const priceContainer = this.closest('.product-details')?.querySelector("product-price");
    if (priceContainer instanceof HTMLElement) {
      priceContainer.style.display = "none";
    }

    const headerHeight = document.querySelector(".header-section")?.offsetHeight || 0;
    const top = upsellWrapper.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
    window.scrollTo({
        top,
        behavior: "smooth",
    });

    upsellSuccessMessage.classList.add("show");

    const upsellSuccessMessageHeight = upsellSuccessMessage.offsetHeight;
    upsellWrapper.style.setProperty("--upsell-success-message-height", `${upsellSuccessMessageHeight}px`);

    const wrapperInner = upsellWrapper.querySelector(".product-upsell__wrapper-inner");

    setTimeout(() => {
      wrapperInner?.classList.add("animate-wrapper-inner-show");
    }, 3500);

    setTimeout(() => {
      upsellSuccessMessage.classList.remove("show");
      wrapperInner?.classList.remove("animate-wrapper-inner-show");
    }, 4250);
  }

  handleHideUpsell() {
    const { productFormButtons, upsellWrapper } = this.refs;
    if (!productFormButtons || !upsellWrapper) return;

    productFormButtons.classList.remove("visually-hidden-always");
    productFormButtons.classList.add("animate-appear");
    upsellWrapper.classList.remove("animate-appear");
    upsellWrapper.classList.add("visually-hidden-always");

    const priceContainer = this.closest('.product-details')?.querySelector("product-price");
    if (priceContainer instanceof HTMLElement) {
      priceContainer.style.display = "block";
    }
  }

  handleAddUpsellToCart(event) {
    const target = event.target?.closest("[data-upsell-add-to-cart]");
    if (!target) {
      return;
    }

    event.preventDefault();

    const form = target.closest("form");
    if (!form) {
      return;
    }

    const formData = new FormData(form);

    const id = formData.get("upsell-id");
    const upsellSellingPlanType = formData.get("upsell-selling-plan-type");
    if (!id || !upsellSellingPlanType) {
      return;
    }

    if (window?.Rebuy?.SmartCart) {
        window.Rebuy.SmartCart.skip_open = false;
    }

    const quantity = parseInt(String(formData.get("upsell-qty") || "1"));

    const properties = {};

    const upsellRebuyWidgetId = formData.get("upsell-rebuy-widget-id");
    if (upsellRebuyWidgetId) {
      properties._attribution = "Rebuy Product";
      properties._source = "Rebuy";
      properties._widget_id = upsellRebuyWidgetId;
    }

    const items = [];

    const item = {
      id: parseInt(String(id)),
      quantity,
      properties,
    };

    if (String(upsellSellingPlanType) === "subscription") {
      const upsellSellingPlanId = formData.get("upsell-selling-plan");
      if (upsellSellingPlanId) {
        item.selling_plan = parseInt(String(upsellSellingPlanId));
      }
    } else if (String(upsellSellingPlanType) === "one-time") {
      item.selling_plan = "";
    }

    items.push(item);

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    /** @type {string[]} */
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(String(item.dataset.sectionId));
      }
    });

    const requestBody =
      /** @type {{items: Array<{id: number, quantity: number, selling_plan?: number | string}>, sections?: string}} */ ({
        items: items,
      });

    if (cartItemComponentsSectionIds.length > 0) {
      requestBody.sections = cartItemComponentsSectionIds.join(",");
    }

    const { addToCartTextError } = this.refs;

    fetch(Theme.routes.cart_add_url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(requestBody),
    })
      .then((response) => response.json())
      .then((response) => {
        console.log(response, 'response');
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(
              form.getAttribute("id") || "",
              response.message,
              response.description,
              response.errors,
            ),
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove("hidden");

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add("hidden");

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: "product-form-component",
              itemCount: quantity,
              productId: this.dataset.productId,
            }),
          );

          return;
        } else {
          if (addToCartTextError) {
            addToCartTextError.classList.add("hidden");
            addToCartTextError.removeAttribute("aria-live");
          }

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton =
              this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector(
              ".add-to-cart-text--added",
            );
            const addedText =
              addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              source: "product-form-component",
              itemCount: quantity,
              productId: this.dataset.productId,
              sections: response.sections,
            }),
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent("add:user-action", event);
      });
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (
      this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute(
        "disabled",
      ) === "true"
    )
      return;

    // Send the add to cart information to the cart
    const form = this.querySelector("form");

    if (!form) throw new Error("Product form element missing");

    const formData = new FormData(form);

    const qtyMultiplier = parseInt(
      String(formData.get("qty-multiplier") || "1"),
    );
    const currentQuantity = parseInt(String(formData.get("quantity") || "1"));
    const multipliedQuantity = currentQuantity * qtyMultiplier;

    const purchaseType = String(formData.get("purchaseType") || "");
    const variantId = parseInt(
      String(this.refs.variantId.value || formData.get("id") || "0"),
    );

    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[([^\]]+)\]$/);
      if (match && typeof value === "string" && value !== "") {
        properties[match[1]] = value;
      }
    }
    const mainProductRebuyWidgetId = formData.get("main-product-rebuy-widget-id");
    if (mainProductRebuyWidgetId) {
      properties._attribution = "Rebuy Product";
      properties._source = "Rebuy";
      properties._widget_id = mainProductRebuyWidgetId;
    }

    const mainItem =
      /** @type {{id: number, quantity: number, selling_plan?: number | string}} */ ({
        id: variantId,
        quantity: multipliedQuantity,
        properties,
      });

    if (purchaseType === "one-time") {
      mainItem.selling_plan = "";
    } else {
      const sellingPlan = formData.get("selling_plan");
      if (sellingPlan) {
        mainItem.selling_plan = parseInt(String(sellingPlan));
      }
    }

    const items = [mainItem];

    const includeUpsell = event.submitter?.dataset?.includeUpsell === "true";
    if (includeUpsell) {
        if (window?.Rebuy?.SmartCart) {
            window.Rebuy.SmartCart.skip_open = true;
        }
    
        this.handleShowUpsell();
    }

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    /** @type {string[]} */
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(String(item.dataset.sectionId));
      }
    });

    const requestBody =
      /** @type {{items: Array<{id: number, quantity: number, selling_plan?: number | string}>, sections?: string}} */ ({
        items: items,
      });

    if (cartItemComponentsSectionIds.length > 0) {
      requestBody.sections = cartItemComponentsSectionIds.join(",");
    }

    fetch(Theme.routes.cart_add_url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(requestBody),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(
              form.getAttribute("id") || "",
              response.message,
              response.description,
              response.errors,
            ),
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove("hidden");

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add("hidden");

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: "product-form-component",
              itemCount:
                multipliedQuantity || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              silent: includeUpsell,
            }),
          );

          return;
        } else {
          if (!variantId) throw new Error("Form ID is required");

          if (addToCartTextError) {
            addToCartTextError.classList.add("hidden");
            addToCartTextError.removeAttribute("aria-live");
          }

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton =
              this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector(
              ".add-to-cart-text--added",
            );
            const addedText =
              addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, variantId.toString(), {
              source: "product-form-component",
              itemCount:
                multipliedQuantity || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
              silent: includeUpsell,
            }),
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent("add:user-action", event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = "";
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton =
      addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector(
      '[ref="addToCartButton"]',
    );

    if (!currentAddToCartButton) return;

    // Update the button state
    if (
      event.detail.resource == null ||
      event.detail.resource.available == false
    ) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute(
        "hidden",
        "true",
      );
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute("hidden");
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);

      if (this.dataset.dynamicPricing === "true") {
        requestAnimationFrame(() => this.updatePrices());
      } else {
        requestAnimationFrame(() => this.updateFreeShippingBadges());
      }
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? "";

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia =
        event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute(
          "data-product-variant-media",
          productVariantMedia + "&width=100",
        );
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get("product-form-component")) {
  customElements.define("product-form-component", ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = "1";

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(
        progress,
        startPoint,
        controlPoint1,
        controlPoint2,
        endPoint,
      );

      //Update the position of the flying thingy
      this.style.setProperty("--x", `${position.x}px`);
      this.style.setProperty("--y", `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty("--scale", `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = "0";
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty("--x", `${startPoint.x}px`);
    this.style.setProperty("--y", `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get("fly-to-cart")) {
  customElements.define("fly-to-cart", FlyToCart);
}
